// RT-3 / Phase 3e cross-plane — Business Web durable installation identity for the AUTH-V1 frame.
//
// `installation_id` is an opaque, per-browser-INSTALLATION identifier: generated once, persisted in
// localStorage, reused across refresh / browser restart / logout / account switch, shared by every tab in
// the same browser profile. It is NOT user identity, NOT tenant identity, NOT the per-socket session/nonce,
// and NOT an auth credential. No fingerprinting, no hardware id, no User-Agent/IP. It lets the backend
// distinguish "same installation, different user" (A->B switch) from "same user, different device".

const STORAGE_KEY = 'servisfy_installation_id';
const MAX_LEN = 128;   // must fit the backend validator (len [8,128], no whitespace/control)
const MIN_LEN = 8;

let _memoryFallback = null;  // used ONLY when localStorage is unavailable (private mode / quota / disabled)

export function isValidInstallationId(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (v.length < MIN_LEN || v.length > MAX_LEN) return false;
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c < 33 || c === 127) return false;  // no space / control / DEL
  }
  return true;
}

function generate() {
  // Preferred: native opaque UUID.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  // Standards-based RFC-4122 v4 fallback via getRandomValues.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
      return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
    }
  } catch (e) { /* fall through */ }
  // Last resort (no crypto at all): opaque, >= MIN_LEN, no whitespace. Not cryptographically strong but
  // never blocks authentication; the backend treats it as an opaque id.
  return `iid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readRaw() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return _memoryFallback;
  }
}

function writeRaw(v) {
  try {
    window.localStorage.setItem(STORAGE_KEY, v);
  } catch (e) {
    _memoryFallback = v;  // storage unavailable -> stable in-memory value for this session
  }
}

export function getStoredInstallationId() {
  const v = readRaw();
  return isValidInstallationId(v) ? v.trim() : null;
}

// Returns the durable installation id, generating + persisting one when absent or when the stored value is
// malformed/oversized. Never throws; never blocks login; never logs the value.
export function getOrCreateInstallationId() {
  const existing = getStoredInstallationId();
  if (existing) {
    _memoryFallback = existing;
    return existing;
  }
  const id = generate();
  writeRaw(id);
  _memoryFallback = id;  // keep a consistent value even if storage silently dropped it
  return id;
}
